import React from 'react';
import { Box, BoxProps } from '@mui/material';
import squareLogoUrl from '../assets/veilstream-square.svg?url';

interface VeilStreamLogoProps extends Omit<BoxProps, 'component'> {
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'header';
  /** When 'icon', renders the square vs_white_transparent logo (e.g. for collapsed sidebar). */
  variant?: 'full' | 'icon';
}

const FULL_LOGO_SRC_DARK = '/logo/Logo-white-on-transparent.svg';
const FULL_LOGO_SRC_LIGHT = '/logo/Logo-black-on-transparent.svg';

const VeilStreamLogo: React.FC<VeilStreamLogoProps> = ({
  size = 'medium',
  variant = 'full',
  sx,
  ...props
}) => {
  const sizeMap = {
    small: { height: '30px', width: '96px' },
    medium: { height: '40px', width: '128px' },
    large: { height: '50px', width: '160px' },
    xlarge: { height: '60px', width: '192px' },
    header: { height: '60px', width: '130px' }
  };

  const selectedSize = sizeMap[size];
  const isIcon = variant === 'icon';
  const dimensions = isIcon
    ? { height: '32px', width: '32px' }
    : { height: selectedSize.height, width: selectedSize.width };

  if (isIcon) {
    return (
      <VeilStreamSquareMark
        size={32}
        sx={sx}
        {...props}
      />
    );
  }

  return (
    <Box
      className="vs-logo-swap"
      sx={{
        ...dimensions,
        position: 'relative',
        flexShrink: 0,
        ...sx
      }}
      {...props}
      role="img"
      aria-label="VeilStream Logo"
    >
      <Box
        component="img"
        className="vs-logo-on-dark"
        src={FULL_LOGO_SRC_DARK}
        alt=""
        aria-hidden
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <Box
        component="img"
        className="vs-logo-on-light"
        src={FULL_LOGO_SRC_LIGHT}
        alt=""
        aria-hidden
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </Box>
  );
};

/** Square wave mark. Native img + pixel sizes so the 2048 SVG cannot expand the flex row. */
export function VeilStreamSquareMark({
  size = 32,
  decorative = false,
  sx,
  ...props
}: Omit<BoxProps, 'component'> & { size?: number; decorative?: boolean }) {
  return (
    <Box
      component="img"
      className="vs-logo-icon"
      src={squareLogoUrl}
      alt={decorative ? '' : 'VeilStream'}
      width={size}
      height={size}
      sx={{
        display: 'block',
        width: `${size}px !important`,
        height: `${size}px !important`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        maxWidth: `${size}px`,
        maxHeight: `${size}px`,
        objectFit: 'contain',
        flexShrink: 0,
        ...sx,
      }}
      {...props}
      {...(decorative ? { 'aria-hidden': true } : {})}
    />
  )
}

export default VeilStreamLogo;
